export type ValidationDetailT = {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
};

export type ErrorBodyT = {
  detail?: string | ValidationDetailT[] | { errors?: ValidationDetailT[]; msg?: string };
  description?: string | Record<string, string>;
  errors?: ValidationDetailT[];
  message?: string;
  msg?: string;
};

export type TransactionErrorT = {
  status?: number;
  body?: ErrorBodyT;
  text?: string;
};

const FIELD_LABELS: Record<string, string> = {
  amount: 'Amount',
  amount_sell: 'Sell',
  amount_buy: 'Buy',
  value_datetime: 'Value date',
  transaction_date: 'Transaction date',
  commission: 'Commission',
  currency: 'Currency',
  comment: 'Comment',
  details: 'Details',
  reference: 'Reference',
};

const parseJsonFragment = (text?: string): ErrorBodyT | undefined => {
  if (typeof text !== 'string') return undefined;

  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');

  if (start === -1 || end <= start) return undefined;

  try {
    return JSON.parse(text.slice(start, end + 1));
  } catch {
    return undefined;
  }
};

const isTechnicalText = (text: string) => text.includes('{') || /status:\s*\d/i.test(text);

const getFieldLabel = (loc?: (string | number)[]): string => {
  if (!Array.isArray(loc)) return '';

  const path = loc.filter((part) => part !== 'body' && typeof part !== 'number');
  const field = String(path[path.length - 1] ?? '');

  if (!field) return '';

  return FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
};

const getValidationMessage = (details?: ValidationDetailT[]): string => {
  if (!Array.isArray(details)) return '';

  return details
    .map((item) => {
      const message = item?.msg || item?.type || '';

      if (!message) return '';

      const label = getFieldLabel(item?.loc);

      return label ? `${label}: ${message}` : message;
    })
    .filter(Boolean)
    .join('; ');
};

const getDescriptionMessage = (description: ErrorBodyT['description']): string => {
  if (typeof description === 'string' && description.trim()) return description;

  if (description && typeof description === 'object') {
    const firstValue = Object.values(description)[0];

    if (typeof firstValue === 'string' && firstValue.trim()) return firstValue;
  }

  return '';
};

const getBodyMessage = (body?: ErrorBodyT): string => {
  if (!body) return '';

  const validationMessage = getValidationMessage(body.errors);

  if (validationMessage) return validationMessage;

  const { detail } = body;

  if (Array.isArray(detail)) {
    const detailValidationMessage = getValidationMessage(detail);

    if (detailValidationMessage) return detailValidationMessage;
  }

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const nestedMessage = getValidationMessage(detail.errors) || detail.msg || '';

    if (nestedMessage) return nestedMessage;
  }

  if (typeof detail === 'string' && detail.trim()) {
    // прокси заворачивает ответ TS в строку вида "TS service. Status: 422, resp: {...}"
    const nestedMessage = getBodyMessage(parseJsonFragment(detail));

    if (nestedMessage) return nestedMessage;

    if (!isTechnicalText(detail)) return detail;
  }

  const descriptionMessage = getDescriptionMessage(body.description);

  if (descriptionMessage) return descriptionMessage;

  const plainMessage = body.message || body.msg || '';

  return plainMessage.trim() && !isTechnicalText(plainMessage) ? plainMessage : '';
};

export const getTransactionErrorMessage = (error: TransactionErrorT, fallbackMessage: string): string =>
  getBodyMessage(error?.body || parseJsonFragment(error?.text)) || fallbackMessage;

export default getTransactionErrorMessage;
